import { AccrediblePageParams } from '@accredible-frontend-v2/models';
import { RecipientApiService } from '@accredible-frontend-v2/recipient-api';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TranscriptCreateRequest, TranscriptResponse } from '../../entities/transcripts/transcript';
import { TranscriptsListResponse } from '../../entities/transcripts/transcripts-list';

export const TranscriptsListSort = {
  ID_ASC: 'id_asc',
  ID_DESC: 'id_desc',
  STATUS_ASC: 'status_asc',
  STATUS_DESC: 'status_desc',
  GENERATED_AT_ASC: 'generated_at_asc',
  GENERATED_AT_DESC: 'generated_at_desc',
  RECEIVER_NAME_ASC: 'receiver_name_asc',
  RECEIVER_NAME_DESC: 'receiver_name_desc',
} as const;

export type TranscriptsListSortType =
  (typeof TranscriptsListSort)[keyof typeof TranscriptsListSort];

export interface TranscriptsListParams extends AccrediblePageParams {
  sort_by?: TranscriptsListSortType;
}

@Injectable({
  providedIn: 'root',
})
export class TranscriptsApiService extends RecipientApiService {
  readonly TRANSCRIPT_API_URL = `/v1/recipient/transcripts`;

  private _buildQueryParams(pageParams: TranscriptsListParams): URLSearchParams {
    const queryParams = new URLSearchParams();

    if (!pageParams) {
      return queryParams;
    }

    Object.entries(pageParams).forEach(([param, value]) => {
      if (!value) return;
      queryParams.set(param, String(value).trim());
    });

    return queryParams;
  }

  loadTranscripts(pageParams: TranscriptsListParams): Observable<TranscriptsListResponse> {
    const queryParams = this._buildQueryParams(pageParams);

    const queryString = queryParams.toString();
    const url = `${this.TRANSCRIPT_API_URL}${queryString ? `?${queryString}` : ''}`;

    return this._get(url).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  loadTranscript(id: string): Observable<TranscriptResponse> {
    const url = `${this.TRANSCRIPT_API_URL}/${id}`;

    return this._get(url).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  createTranscript(transcript: TranscriptCreateRequest): Observable<TranscriptResponse> {
    return this._post(this.TRANSCRIPT_API_URL, { transcript }).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }
}
